import React, { useEffect, useMemo } from 'react'
import { CircularProgress, MenuItem, Select, SvgIcon, Typography } from '@mui/material'

import { useRemoteSelectOptions } from '@/hooks/use-remote-select-options'
import { useLocaleStore } from '@/store/locale'
import { findFieldIdByAdminLabel, parseRemoteSelectConfig } from '@/utils/form/remote-select'

import { WPCF7Form, WPCF7FormField } from '@/types'

export type SelectFieldProps = {
  field: WPCF7Form['form'][0]
  formik: any
  allFields?: WPCF7FormField[]
}

const SelectField = ({ field, formik, allFields = [] }: SelectFieldProps) => {
  const currentLocale = useLocaleStore((state) => state.currentLocale) || 'it'

  // Parse remote configuration from CSS classes
  const remoteConfig = useMemo(() => parseRemoteSelectConfig(field), [field])

  // Find the dependency field ID if this field depends on another
  const dependencyFieldId = useMemo(() => {
    if (!remoteConfig.dependsOn || allFields.length === 0) return null
    return findFieldIdByAdminLabel(allFields, remoteConfig.dependsOn)
  }, [remoteConfig.dependsOn, allFields])

  // Get dependency value from formik
  const dependencyValue = dependencyFieldId ? formik.values[`input_${dependencyFieldId}`] : null

  // Fetch remote options
  const { choices: remoteChoices, isLoading: isLoadingRemote } = useRemoteSelectOptions({
    remoteKey: remoteConfig.remoteKey,
    dependsOn: remoteConfig.dependsOn,
    dependencyValue,
    prependOption: remoteConfig.prependOption,
    enabled: remoteConfig.isRemote
  })

  // Use remote choices if remote, otherwise use field.choices
  const choices = remoteConfig.isRemote ? remoteChoices : field.choices || []

  // Determine if field should be disabled
  const isDisabled =
    formik.isSubmitting ||
    isLoadingRemote ||
    (remoteConfig.isRemote && remoteConfig.dependsOn && !dependencyValue)

  // Reset field value when dependency changes
  useEffect(() => {
    if (remoteConfig.dependsOn && dependencyFieldId) {
      // Reset this field's value when dependency changes
      const currentValue = formik.values[`input_${field.id}`]
      if (currentValue) {
        formik.setFieldValue(`input_${field.id}`, '', false)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dependencyValue])

  return (
    <>
      {field.label && field.labelPlacement !== 'hidden_label' && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            display: 'block',
            mb: 1,
            ...(formik.submitCount > 0 &&
              formik.errors[`input_${field.id}`] !== undefined &&
              formik.touched[`input_${field.id}`] !== undefined && {
                color: 'error.main'
              })
          }}
        >
          {field.label}
          {field.isRequired ? ' *' : ''}
        </Typography>
      )}
      <Select
        labelId={`input_${field.id}-label`}
        id={`input_${field.id}`}
        value={formik.values[`input_${field.id}`]}
        label={field.label}
        disabled={isDisabled}
        variant="standard"
        defaultValue={field.placeholder}
        fullWidth
        onChange={(e) => {
          formik.setFieldValue(`input_${field.id}`, e.target.value, false)
        }}
        onBlur={formik.handleBlur}
        IconComponent={({ open }) =>
          isLoadingRemote ? (
            <CircularProgress size={16} sx={{ mr: 1 }} />
          ) : (
            <SvgIcon
              fontSize="small"
              sx={{ fill: (theme) => theme.palette.text.secondary }}
              style={{
                transform: open ? 'rotate(180deg)' : 'none',
                transition: 'transform 0.2s'
              }}
            >
              <use href="#ico-arrow-down" />
            </SvgIcon>
          )
        }
        sx={() => ({
          '&.MuiInputBase-root:before': {
            borderBottomColor: 'rgba(0, 0, 0, 0.1)'
          },
          '&.MuiInputBase-root:hover:before': {
            borderBottomColor: 'rgba(0, 0, 0, 0.4)'
          },
          '&.MuiInputBase-root:after': {
            borderBottomColor: 'rgba(0, 0, 0, 0.4)'
          },
          '&.MuiInputBase-root.Mui-error:after': {
            borderBottomColor: 'error.main'
          },
          ...(formik.submitCount > 0 &&
            formik.errors[`input_${field.id}`] !== undefined &&
            formik.touched[`input_${field.id}`] !== undefined && {
              '&.MuiInputBase-root:before': {
                borderBottomColor: 'error.main'
              },
              '&.MuiInputBase-root:hover:before': {
                borderBottomColor: 'error.main'
              },
              '&.MuiInputBase-root:after': {
                borderBottomColor: 'error.main'
              }
            }),
          ...(isDisabled && {
            pointerEvents: 'none',
            opacity: 0.4
          })
        })}
      >
        {choices.map((choice) => (
          <MenuItem key={choice.value} value={choice.value}>
            {typeof choice.text === 'string'
              ? choice.text
              : (choice.text as any)?.[currentLocale] || (choice.text as any)?.it || ''}
          </MenuItem>
        ))}
      </Select>
      {formik.submitCount > 0 &&
        formik.touched[`input_${field.id}`] &&
        formik.errors[`input_${field.id}`] && (
          <Typography variant="caption" color="error" sx={{ mt: 1 }}>
            {String(formik.errors[`input_${field.id}`])}
          </Typography>
        )}
    </>
  )
}

export default SelectField
